import React, { useState, useCallback, useMemo, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Upload, FileSpreadsheet, PlayCircle, AlertCircle, CheckCircle2, Users, Clock, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { formatMoney, currencyLabel } from '../lib/currency';

const CURRENCY = 'USD';

function KpiCard({ icon: Icon, label, value, sub, tone = 'default' }) {
  const tones = {
    default: 'bg-white',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    blue: 'bg-blue-50 border-blue-200',
    amber: 'bg-amber-50 border-amber-200',
  };
  return (
    <div className={`rounded-lg border p-4 ${tones[tone] || tones.default}`}>
      <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wide">
        {Icon && <Icon className="w-4 h-4" />}
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-900 mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function formatCurrency(v) {
  return formatMoney(v, CURRENCY);
}

export default function PayrollSimulator() {
  const [file, setFile] = useState(null);
  const [defaultBaseSalary, setDefaultBaseSalary] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [showUnmatchedOnly, setShowUnmatchedOnly] = useState(false);
  const [showMatchedOnly, setShowMatchedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const fileRef = useRef(null);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    if (e.dataTransfer?.files?.length) {
      setFile(e.dataTransfer.files[0]);
    }
  }, []);

  const runSimulation = async () => {
    if (!file) {
      toast.error('Choisissez d\'abord un fichier');
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const params = {};
      if (defaultBaseSalary && parseFloat(defaultBaseSalary) > 0) {
        params.default_base_salary = parseFloat(defaultBaseSalary);
      }
      const r = await api.post('/payroll/simulate-from-punch-file', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        params,
      });
      setResult(r.data);
      toast.success(`Simulation OK : ${r.data.matched_employees} employe(s) reconnus sur ${r.data.total_employees_in_file}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la simulation');
    } finally {
      setRunning(false);
    }
  };

  const filteredSims = useMemo(() => {
    if (!result) return [];
    let list = result.simulations;
    if (showMatchedOnly) list = list.filter((s) => s.matched);
    if (showUnmatchedOnly) list = list.filter((s) => !s.matched);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (s) =>
          (s.employee_name || '').toLowerCase().includes(q) ||
          (s.name_in_file || '').toLowerCase().includes(q) ||
          (s.zk_id || '').includes(q) ||
          (s.matricule || '').toLowerCase().includes(q)
      );
    }
    return list.sort((a, b) => (b.net_estimated || 0) - (a.net_estimated || 0));
  }, [result, showMatchedOnly, showUnmatchedOnly, search]);

  return (
    <div className="space-y-6" data-testid="payroll-simulator-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="w-8 h-8 text-blue-600" />
            Simulation de paie
          </h1>
          <p className="text-slate-500 mt-1">
            Uploadez le fichier d&apos;export de votre pointeuse ZKTeco et generez une simulation de paie
            sur la periode couverte. <strong>Aucune fiche de paie n&apos;est creee en base</strong>.
          </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">1. Charger le fichier de pointage</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            onDrop={onDrop}
            onDragOver={(e) => e.preventDefault()}
            className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center bg-slate-50 hover:bg-slate-100 transition"
            data-testid="payroll-sim-drop"
          >
            <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">
              Glissez le fichier ici, ou cliquez pour parcourir
            </p>
            <p className="text-xs text-slate-500 mt-1">Formats acceptes : .xls, .xlsx (max 5 Mo)</p>
            <input
              ref={fileRef}
              type="file"
              accept=".xls,.xlsx"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="payroll-sim-input"
            />
            <Button
              type="button"
              variant="outline"
              className="mt-4"
              onClick={() => fileRef.current?.click()}
              data-testid="payroll-sim-browse"
            >
              Parcourir
            </Button>
            {file && (
              <div className="mt-4 inline-flex items-center gap-2 bg-white px-3 py-2 rounded border border-slate-200 text-sm">
                <FileSpreadsheet className="w-4 h-4 text-blue-600" />
                <span className="font-medium">{file.name}</span>
                <span className="text-slate-400">({Math.round(file.size / 1024)} Ko)</span>
              </div>
            )}
          </div>
          <div className="mt-5 pt-4 border-t border-slate-200">
            <Label className="text-sm font-semibold text-slate-800">
              Salaire de base mensuel ({currencyLabel(CURRENCY)})
            </Label>
            <p className="text-xs text-slate-500 mt-1 mb-2">
              Applique a <strong>tous</strong> les employes pour la simulation. Laissez vide pour
              utiliser le salaire individuel de chaque employe (Employee.base_salary).
            </p>
            <Input
              type="number"
              min="0"
              step="10"
              placeholder="Ex: 800"
              value={defaultBaseSalary}
              onChange={(e) => setDefaultBaseSalary(e.target.value)}
              className="max-w-xs"
              data-testid="payroll-sim-base-salary"
            />
          </div>
          <div className="flex justify-end mt-4">
            <Button
              onClick={runSimulation}
              disabled={!file || running}
              size="lg"
              data-testid="payroll-sim-run"
            >
              <PlayCircle className="w-5 h-5 mr-2" />
              {running ? 'Simulation en cours...' : 'Lancer la simulation'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {result && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCard
              icon={Calendar}
              label="Periode"
              value={`${result.days_covered} jours`}
              sub={`${result.period_start} -> ${result.period_end}`}
              tone="blue"
            />
            <KpiCard
              icon={Users}
              label="Employes"
              value={`${result.matched_employees} / ${result.total_employees_in_file}`}
              sub={`${result.unmatched_count} non reconnus`}
              tone="green"
            />
            <KpiCard
              icon={Clock}
              label="Retards / Absences"
              value={result.totals.late_days + ' / ' + result.totals.absent_days}
              sub={`${result.totals.worked_days} jours travailles`}
              tone="amber"
            />
            <KpiCard
              icon={CheckCircle2}
              label="Masse simulee"
              value={formatCurrency(result.totals.net_estimated)}
              sub={`Brut ${formatCurrency(result.totals.gross_pay)}`}
              tone="green"
            />
          </div>

          {result.unmatched_count > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-amber-900">
                      {result.unmatched_count} employes du fichier ne sont pas reconnus en base
                    </p>
                    <p className="text-amber-700 mt-1">
                      Ils sont identifies par leur ID pointeuse (ex :{' '}
                      {result.unmatched_employees
                        .slice(0, 5)
                        .map((u) => u.zk_id)
                        .join(', ')}
                      ...). Renseignez le champ <code>biometric_id</code> sur les fiches employe pour
                      les rattacher automatiquement lors de la prochaine simulation.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center justify-between">
                <span>2. Detail par employe</span>
                <span className="text-sm font-normal text-slate-500">
                  {filteredSims.length} ligne(s)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-4">
                <Input
                  placeholder="Rechercher (nom, matricule, ID pointeuse)..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="max-w-xs"
                  data-testid="payroll-sim-search"
                />
                <Button
                  variant={showMatchedOnly ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setShowMatchedOnly((v) => !v);
                    setShowUnmatchedOnly(false);
                  }}
                  data-testid="payroll-sim-matched-filter"
                >
                  Reconnus uniquement
                </Button>
                <Button
                  variant={showUnmatchedOnly ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setShowUnmatchedOnly((v) => !v);
                    setShowMatchedOnly(false);
                  }}
                  data-testid="payroll-sim-unmatched-filter"
                >
                  Non reconnus uniquement
                </Button>
              </div>
              <div className="rounded-md border border-slate-200 overflow-x-auto">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead className="font-semibold">ID</TableHead>
                      <TableHead className="font-semibold">Employe</TableHead>
                      <TableHead className="font-semibold">Statut</TableHead>
                      <TableHead className="font-semibold text-right">Jours travailles</TableHead>
                      <TableHead className="font-semibold text-right">Retards</TableHead>
                      <TableHead className="font-semibold text-right">Absences</TableHead>
                      <TableHead className="font-semibold text-right">H sup.</TableHead>
                      <TableHead className="font-semibold text-right">Brut</TableHead>
                      <TableHead className="font-semibold text-right">Net simule</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSims.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                          Aucun resultat
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSims.map((s) => (
                        <TableRow key={s.zk_id + '-' + (s.employee_id || 'nx')} className="hover:bg-slate-50">
                          <TableCell className="font-mono text-sm text-slate-500">
                            {s.zk_id}
                          </TableCell>
                          <TableCell>
                            <div className="font-medium">{s.employee_name || s.name_in_file}</div>
                            {s.matricule && (
                              <div className="text-xs text-slate-500">Matricule {s.matricule}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            {s.matched ? (
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                Reconnu
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                Non reconnu
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">{s.worked_days}</TableCell>
                          <TableCell className="text-right">
                            {s.late_days > 0 ? (
                              <span className="text-orange-600 font-medium">
                                {s.late_days} ({s.total_late_minutes}min)
                              </span>
                            ) : (
                              <span className="text-slate-400">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {s.absent_days > 0 ? (
                              <span className="text-red-600 font-medium">{s.absent_days}</span>
                            ) : (
                              <span className="text-slate-400">0</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right text-slate-600">
                            {s.total_overtime_hours > 0 ? `${s.total_overtime_hours}h` : '-'}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            {formatCurrency(s.gross_pay)}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm font-semibold">
                            {formatCurrency(s.net_estimated)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
